import { Trans, useTranslation } from 'react-i18next'

import { SOCIAL_PROOF_IMAGE_VARIANTS } from '~/pages/anatomy/socialProof/libs/components/SocialProof/constants'
import { Styles as S } from '~/pages/anatomy/socialProof/libs/components/SocialProof/styles'
import { useBulkSelector } from '~/shared/hooks/useBulkSelector'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { selectAnswers } from '~/shared/store/common/selects'

const GENDER_I18N_MAP: Record<string, string> = {
  male: 'onboarding.anatomySocialProof.gender.male',
  female: 'onboarding.anatomySocialProof.gender.female',
  other: 'onboarding.anatomySocialProof.gender.other',
}

const AGE_I18N_MAP: Record<string, string> = {
  20: 'onboarding.anatomySocialProof.age.under25',
  30: 'onboarding.anatomySocialProof.age.between2534',
  40: 'onboarding.anatomySocialProof.age.between3544',
  50: 'onboarding.anatomySocialProof.age.between4554',
  60: 'onboarding.anatomySocialProof.age.between5564',
  65: 'onboarding.anatomySocialProof.age.over65',
}

const getAgeGroup = (age: string): string => {
  if (age === '20') return '18-24'
  if (age === '30' || age === '40') return '25-44'
  if (age === '50' || age === '60') return '45-64'
  return '65-plus'
}

interface ISocialProofProps {
  nextPagePath: string
  pageId: string
}

const SocialProof = ({ nextPagePath, pageId }: ISocialProofProps) => {
  const { t } = useTranslation('anatomy')
  const { user } = useBulkSelector({ user: selectAnswers })

  const genderKey = String(user?.['gender-1'] ?? 'female')
  const ageKey = String(user?.['age-1'] ?? '30')

  const genderText = t(
    (typeof user?.['gender-1'] === 'string'
      ? GENDER_I18N_MAP[user['gender-1']]
      : undefined) ?? GENDER_I18N_MAP.other,
  )
  const ageText = t(
    (typeof user?.['age-1'] === 'string'
      ? AGE_I18N_MAP[user['age-1']]
      : undefined) ?? AGE_I18N_MAP[30],
  )

  const gender = genderKey === 'male' ? 'male' : 'female'
  const ageGroup = getAgeGroup(ageKey)
  const variantKey = `${gender}-${ageGroup}`
  const image =
    SOCIAL_PROOF_IMAGE_VARIANTS[variantKey] ??
    SOCIAL_PROOF_IMAGE_VARIANTS['female-25-44']

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: `You're exactly where you belong`,
  })

  return (
    <S.Root>
      <S.StyledContainer>
        <S.ImageWrapper>
          <picture>
            <source srcSet={image.webp} type="image/webp" />
            <img src={image.png} alt="You belong here" />
          </picture>
        </S.ImageWrapper>

        <S.Title>
          <Trans ns="anatomy" i18nKey="onboarding.anatomySocialProof.title" />
        </S.Title>

        <S.Subtitle>
          <Trans
            ns="anatomy"
            i18nKey="onboarding.anatomySocialProof.subtitle"
            values={{ genderText, ageText }}
            components={{ strong: <strong /> }}
          />
        </S.Subtitle>

        <S.StyledButton
          type="button"
          onClick={() => handleContinue(`${genderText} ${ageText}`.trim())}
        >
          <Trans i18nKey="actions.continue" />
        </S.StyledButton>
      </S.StyledContainer>
    </S.Root>
  )
}

export default SocialProof
